import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Snackbar,
  Alert,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  Paper,
  Chip,
  InputAdornment,
} from "@mui/material";
import {
  fetchInventoryItem,
  updateInventoryItemConfig,
  getAllCategories,
} from "../../redux/actions/InventoryAction";
import HeadrailConfigEdit from "./HeadrailConfigEdit";
import ProDependency from "./ProDependency";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EditIcon from "@mui/icons-material/Edit";
import InventoryIcon from "@mui/icons-material/Inventory";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CategoryIcon from "@mui/icons-material/Category";
import WarningIcon from "@mui/icons-material/Warning";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

const StockUpdateModal = ({
  open,
  onClose,
  currentStock,
  onSubmit,
  itemName,
  loading,
}) => {
  const [newStock, setNewStock] = useState(currentStock || 0);
  const [error, setError] = useState("");

  useEffect(() => {
    setNewStock(currentStock || 0);
  }, [currentStock]);

  const handleStockChange = (e) => {
    const value = e.target.value;
    if (value === "" || (Number(value) >= 0 && !isNaN(value))) {
      setNewStock(value);
      setError("");
    } else {
      setError("Stock must be a non-negative number");
    }
  };

  const handleSubmit = () => {
    if (newStock === "" || Number(newStock) < 0) {
      setError("Please enter a valid non-negative number");
      return;
    }
    onSubmit(Number(newStock));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          bgcolor: "#1a2332",
          color: "white",
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
        },
      }}
    >
      <DialogTitle
        sx={{
          color: "white",
          borderBottom: "1px solid #2a3441",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <InventoryIcon color="primary" />
        Update Stock for {itemName}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <TextField
          label="In Stock"
          type="number"
          value={newStock}
          onChange={handleStockChange}
          fullWidth
          margin="normal"
          variant="outlined"
          InputLabelProps={{
            style: { color: "#94a3b8" },
            shrink: true,
          }}
          InputProps={{
            style: {
              backgroundColor: "#2a3441",
              color: "white",
              borderRadius: 8,
            },
            startAdornment: (
              <InputAdornment position="start">
                <InventoryIcon sx={{ color: "#64748b" }} />
              </InputAdornment>
            ),
          }}
          error={!!error}
          helperText={error}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "#475569" },
              "&:hover fieldset": { borderColor: "#64748b" },
              "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            color: "#94a3b8",
            "&:hover": { backgroundColor: "#374151" },
          }}
          startIcon={<CloseIcon />}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
          sx={{
            bgcolor: "#3b82f6",
            "&:hover": { bgcolor: "#2563eb" },
          }}
        >
          {loading ? "Updating..." : "Update Stock"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EditInventoryItem = ({ inventoryId }) => {
  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;
  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
  } = useSelector((state) => state.getCategories);
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [stockUpdateLoading, setStockUpdateLoading] = useState(false);
  const [config, setConfig] = useState({
    headrail: {
      sleeveOn: { isTrue: false, addonPrice: 0 },
      sleeveOff: { isTrue: false },
    },
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [deductionMethod, setDeductionMethod] = useState("");
  const [proDependency, setProDependency] = useState([]);
  const [stockModalOpen, setStockModalOpen] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (userInfo?.token) {
          dispatch(getAllCategories(userInfo.token));
        }
        const fetchedItem = await fetchInventoryItem(
          inventoryId,
          userInfo.token
        );
        if (!fetchedItem.config || fetchedItem.config === 0) {
          fetchedItem.config = {
            headrail: {
              sleeveOn: { isTrue: false, addonPrice: 0 },
              sleeveOff: { isTrue: false },
            },
          };
        }
        setDeductionMethod(fetchedItem.deductionMethod || "");
        setItem(fetchedItem);
        setConfig(fetchedItem.config);
        setProDependency(fetchedItem.proDependency || []);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching inventory item:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, [inventoryId, userInfo.token, dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdateLoading(true);
    try {
      const dataToSend = {
        config,
        deductionMethod,
        category: item.category,
        name: item.name,
        proDependency: proDependency,
        sellingPrice: item.sellingPrice,
        unitType: item.unitType,
        inStock: item.inStock,
      };
      await updateInventoryItemConfig(inventoryId, dataToSend, userInfo.token);
      setSnackbarMessage("Configuration updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error updating inventory item:", error);
      setSnackbarMessage("Failed to update configuration.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleStockUpdate = async (newStock) => {
    setStockUpdateLoading(true);
    try {
      const dataToSend = {
        config,
        deductionMethod,
        category: item.category,
        name: item.name,
        proDependency: proDependency,
        sellingPrice: item.sellingPrice,
        unitType: item.unitType,
        inStock: newStock,
      };
      await updateInventoryItemConfig(inventoryId, dataToSend, userInfo.token);
      setItem((prev) => ({ ...prev, inStock: newStock }));
      setStockModalOpen(false);
      setSnackbarMessage("Stock updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error updating stock:", error);
      setSnackbarMessage("Failed to update stock.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setStockUpdateLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      if (name === "sleeveOn") {
        setConfig((prevConfig) => ({
          ...prevConfig,
          headrail: {
            ...prevConfig.headrail,
            sleeveOn: { ...prevConfig.headrail.sleeveOn, isTrue: checked },
            sleeveOff: { ...prevConfig.headrail.sleeveOff, isTrue: !checked },
          },
        }));
      } else if (name === "sleeveOff") {
        setConfig((prevConfig) => ({
          ...prevConfig,
          headrail: {
            ...prevConfig.headrail,
            sleeveOff: { ...prevConfig.headrail.sleeveOff, isTrue: checked },
            sleeveOn: { ...prevConfig.headrail.sleeveOn, isTrue: !checked },
          },
        }));
      }
    } else {
      if (name === "sellingPrice") {
        setItem((prev) => ({ ...prev, sellingPrice: parseFloat(value) || 0 }));
      } else if (name === "category") {
        setItem((prev) => ({ ...prev, category: value }));
      } else if (name === "unitType") {
        setItem((prev) => ({ ...prev, unitType: value }));
      } else {
        setConfig((prevConfig) => ({
          ...prevConfig,
          headrail: {
            ...prevConfig.headrail,
            [name]: {
              ...prevConfig.headrail[name],
              addonPrice: parseFloat(value) || 0,
            },
          },
        }));
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: "#3b82f6" }} size={60} />
        <Typography sx={{ color: "#94a3b8" }}>
          Loading inventory item...
        </Typography>
      </Box>
    );
  }

  if (!item) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <WarningIcon sx={{ color: "#ef4444", fontSize: 60 }} />
        <Typography sx={{ color: "white", fontSize: "1.2rem" }}>
          Item not found
        </Typography>
      </Box>
    );
  }

  if (categoriesError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <WarningIcon sx={{ color: "#ef4444", fontSize: 60 }} />
        <Typography sx={{ color: "white", fontSize: "1.2rem" }}>
          Error: {categoriesError}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#0f172a",
        p: 3,
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        {/* Header */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            bgcolor: "#1e293b",
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <InventoryIcon sx={{ color: "#3b82f6", fontSize: 32 }} />
            <Typography variant="h4" sx={{ color: "white", fontWeight: 600 }}>
              Edit Inventory Item
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ color: "#94a3b8", mb: 1 }}>
            {item.name}
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Chip
              label={`Stock: ${item.inStock}`}
              color={item.inStock > item.stockAlert ? "success" : "error"}
              variant="outlined"
              sx={{ color: "white" }}
            />
            <Chip
              label={`Category: ${item.category}`}
              color="primary"
              variant="outlined"
              sx={{ color: "white" }}
            />
            <Chip
              label={`Unit: ${item.unitType}`}
              color="secondary"
              variant="outlined"
              sx={{ color: "white" }}
            />
          </Box>
        </Paper>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Item Details Section */}
            <Grid item xs={12} lg={8}>
              <Card
                sx={{
                  bgcolor: "#1e293b",
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <CategoryIcon sx={{ color: "#10b981" }} />
                    <Typography
                      variant="h6"
                      sx={{ color: "#10b981", fontWeight: 600 }}
                    >
                      Item Details
                    </Typography>
                  </Box>
                  <Divider sx={{ bgcolor: "#374151", mb: 3 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={item.name}
                        onChange={(e) =>
                          setItem((prev) => ({ ...prev, name: e.target.value }))
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "#2a3441",
                            color: "white",
                            "& fieldset": { borderColor: "#475569" },
                            "&:hover fieldset": { borderColor: "#64748b" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3b82f6",
                            },
                          },
                          "& .MuiInputLabel-root": { color: "#94a3b8" },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Select
                        fullWidth
                        name="category"
                        value={categoriesLoading ? "" : item.category || ""}
                        onChange={handleChange}
                        displayEmpty
                        disabled={categoriesLoading}
                        sx={{
                          color: "white",
                          bgcolor: "#2a3441",
                          "& .MuiSelect-icon": { color: "white" },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#475569",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#64748b",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3b82f6",
                          },
                          "&.Mui-disabled": {
                            color: "white",
                            "& .MuiSelect-icon": { color: "white" },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#475569",
                            },
                          },
                        }}
                        renderValue={(value) =>
                          categoriesLoading ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CircularProgress
                                size={20}
                                sx={{ color: "white" }}
                              />
                              <span>Loading...</span>
                            </Box>
                          ) : value ? (
                            categories?.find((cat) => cat.value === value)
                              ?.name || "Select Category"
                          ) : (
                            "Select Category"
                          )
                        }
                      >
                        <MenuItem value="" disabled>
                          Select Category
                        </MenuItem>
                        {categories?.map((cat) => (
                          <MenuItem key={cat.value} value={cat.value}>
                            {cat.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        value={item.description || ""}
                        multiline
                        rows={4}
                        disabled
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "#2a3441",
                            color: "white", // Changed to white for better visibility
                            "& fieldset": { borderColor: "#475569" },
                          },
                          "& .MuiInputLabel-root": { color: "#94a3b8" },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography
                            sx={{ color: "#94a3b8", fontSize: "0.875rem" }}
                          >
                            Unit Type
                          </Typography>
                          <Tooltip
                            title={
                              <span style={{ fontSize: ".8rem" }}>
                                In which unit is the item measured/sold (e.g.,
                                The headrail is measured per foot, therefore,
                                the{" "}
                                <span className="text-warning">Unit Type</span>{" "}
                                will be in feet)
                              </span>
                            }
                            arrow
                          >
                            <IconButton size="small" sx={{ color: "#94a3b8" }}>
                              <HelpOutlineIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Select
                          fullWidth
                          name="unitType"
                          value={item.unitType || ""} // Ensure default value is set
                          onChange={handleChange}
                          displayEmpty
                          sx={{
                            color: "white",
                            bgcolor: "#2a3441",
                            "& .MuiSelect-icon": { color: "white" },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#475569",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#64748b",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#3b82f6",
                            },
                          }}
                          renderValue={
                            (value) => (value ? value : "Select Unit Type") // Display default placeholder if no value
                          }
                        >
                          <MenuItem value="" disabled>
                            Select Unit Type
                          </MenuItem>
                          {item.category === "Headrail" ? (
                            <>
                              <MenuItem value="SQFT">
                                Square Foot (SQFT)
                              </MenuItem>
                              <MenuItem value="Foot">Foot</MenuItem>
                              <MenuItem value="Units">
                                Units (Individually)
                              </MenuItem>
                            </>
                          ) : (
                            <>
                              <MenuItem value="PCS">Pieces (PCS)</MenuItem>
                              <MenuItem value="EA">Each (EA)</MenuItem>
                              <MenuItem value="U">Unit (U)</MenuItem>
                              <MenuItem value="Units">
                                Units (Individually)
                              </MenuItem>
                              <MenuItem value="DOZ">Dozen (DOZ)</MenuItem>
                              <MenuItem value="PK">Pack (PK)</MenuItem>
                              <MenuItem value="CTN">Carton (CTN)</MenuItem>
                              <MenuItem value="CS">Case (CS)</MenuItem>
                              <MenuItem value="M">Meter (M)</MenuItem>
                              <MenuItem value="CM">Centimeter (CM)</MenuItem>
                              <MenuItem value="FT">Foot (FT)</MenuItem>
                              <MenuItem value="YD">Yard (YD)</MenuItem>
                              <MenuItem value="IN">Inch (IN)</MenuItem>
                              <MenuItem value="SQFT">
                                Square Foot (SQFT)
                              </MenuItem>
                            </>
                          )}
                        </Select>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography
                            sx={{ color: "#94a3b8", fontSize: "0.875rem" }}
                          >
                            In Stock
                          </Typography>
                          <Tooltip
                            title="Click the edit icon to update the current stock quantity for this item."
                            arrow
                          >
                            <IconButton
                              size="small"
                              sx={{ color: "#3b82f6" }}
                              onClick={() => setStockModalOpen(true)}
                            >
                              <EditIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <TextField
                          fullWidth
                          type="number"
                          value={item.inStock}
                          disabled
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <InventoryIcon sx={{ color: "#64748b" }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            color: "white",
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "#2a3441",
                              color: "white",
                              "& fieldset": { borderColor: "#475569" },
                              "&.Mui-disabled": {
                                color: "white",
                                "& .MuiInputBase-input": {
                                  color: "white",
                                  "-webkit-text-fill-color": "white",
                                },
                              },
                            },
                            "& .MuiInputLabel-root": { color: "#94a3b8" },
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Stock Alert"
                        type="number"
                        value={item.stockAlert}
                        disabled
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <WarningIcon sx={{ color: "#f59e0b" }} />
                            </InputAdornment>
                          ),
                        }}
                              sx={{
                            color: "white",
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "#2a3441",
                              color: "white",
                              "& fieldset": { borderColor: "#475569" },
                              "&.Mui-disabled": {
                                color: "white",
                                "& .MuiInputBase-input": {
                                  color: "white",
                                  "-webkit-text-fill-color": "white",
                                },
                              },
                            },
                            "& .MuiInputLabel-root": { color: "#94a3b8" },
                          }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Cost per Unit"
                        type="number"
                        value={item.cost}
                        disabled
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoneyIcon sx={{ color: "#10b981" }} />
                            </InputAdornment>
                          ),
                        }}
                              sx={{
                            color: "white",
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "#2a3441",
                              color: "white",
                              "& fieldset": { borderColor: "#475569" },
                              "&.Mui-disabled": {
                                color: "white",
                                "& .MuiInputBase-input": {
                                  color: "white",
                                  "-webkit-text-fill-color": "white",
                                },
                              },
                            },
                            "& .MuiInputLabel-root": { color: "#94a3b8" },
                          }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Selling Price"
                        type="number"
                        name="sellingPrice"
                        value={item.sellingPrice || ""}
                        onChange={handleChange}
                        placeholder="Enter selling price"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoneyIcon sx={{ color: "#3b82f6" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "#2a3441",
                            color: "white",
                            "& fieldset": { borderColor: "#475569" },
                            "&:hover fieldset": { borderColor: "#64748b" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3b82f6",
                            },
                          },
                          "& .MuiInputLabel-root": { color: "#94a3b8" },
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Stock Summary Card */}
            <Grid item xs={12} lg={4}>
              <Card
                sx={{
                  bgcolor: "#1e293b",
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                  mb: 3,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: "#10b981", mb: 2, fontWeight: 600 }}
                  >
                    Stock Summary
                  </Typography>
                  <Divider sx={{ bgcolor: "#374151", mb: 2 }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography sx={{ color: "#94a3b8" }}>
                      Current Stock:
                    </Typography>
                    <Typography sx={{ color: "white", fontWeight: 600 }}>
                      {item.inStock} {item.unitType}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography sx={{ color: "#94a3b8" }}>
                      Alert Level:
                    </Typography>
                    <Typography sx={{ color: "#f59e0b", fontWeight: 600 }}>
                      {item.stockAlert} {item.unitType}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography sx={{ color: "#94a3b8" }}>Status:</Typography>
                    <Chip
                      label={
                        item.inStock > item.stockAlert
                          ? "In Stock"
                          : "Low Stock"
                      }
                      color={
                        item.inStock > item.stockAlert ? "success" : "error"
                      }
                      size="small"
                      sx={{ color: "white" }}
                    />
                  </Box>

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setStockModalOpen(true)}
                    startIcon={<EditIcon />}
                    sx={{
                      mt: 2,
                      color: "#3b82f6",
                      borderColor: "#3b82f6",
                      "&:hover": {
                        borderColor: "#2563eb",
                        backgroundColor: "rgba(59, 130, 246, 0.1)",
                      },
                    }}
                  >
                    Update Stock
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Configuration Section */}
            <Grid item xs={12}>
              <Card
                sx={{
                  bgcolor: "#1e293b",
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: "#8b5cf6", mb: 2, fontWeight: 600 }}
                  >
                    Configuration
                  </Typography>
                  <Divider sx={{ bgcolor: "#374151", mb: 3 }} />

                  <Grid container spacing={3}>
                    {item.category === "Headrail" && (
                      <Grid item xs={12}>
                        <HeadrailConfigEdit
                          formData={config.headrail}
                          handleChange={handleChange}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <Select
                        fullWidth
                        name="deductionMethod"
                        value={deductionMethod}
                        onChange={(e) => setDeductionMethod(e.target.value)}
                        displayEmpty
                        sx={{
                          color: "white",
                          bgcolor: "#2a3441",
                          "& .MuiSelect-icon": { color: "white" },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#475569",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#64748b",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#3b82f6",
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          Select Deduction Method
                        </MenuItem>
                        <MenuItem value="width">Width</MenuItem>
                        <MenuItem value="height">Height</MenuItem>
                        <MenuItem value="sqft">Square Feet (sqft)</MenuItem>
                      </Select>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <ProDependency
                        proDep={proDependency}
                        setProDep={setProDependency}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={updateLoading}
                  startIcon={
                    updateLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  sx={{
                    bgcolor: "#10b981",
                    color: "white",
                    px: 4,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    "&:hover": {
                      bgcolor: "#059669",
                    },
                    "&:disabled": {
                      bgcolor: "#374151",
                      color: "#9ca3af",
                    },
                  }}
                >
                  {updateLoading ? "Saving Changes..." : "Save Changes"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        {/* Stock Update Modal */}
        <StockUpdateModal
          open={stockModalOpen}
          onClose={() => setStockModalOpen(false)}
          currentStock={item.inStock}
          onSubmit={handleStockUpdate}
          itemName={item.name}
          loading={stockUpdateLoading}
        />

        {/* Snackbar for alerts */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{
              width: "100%",
              color: "white",
              bgcolor: snackbarSeverity === "success" ? "#10b981" : "#ef4444",
              "& .MuiAlert-icon": {
                color: "white",
              },
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default EditInventoryItem;
